import { AfterViewInit, Component, ElementRef, NgZone, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { LoginService } from './login.service';
import { AuthService } from '../../services/auth.service';
import { GoogleIdentityService } from '../../services/google-identity.service';
import { throwError } from 'rxjs';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CustomFooterComponent } from '../custom-footer/custom-footer.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

interface LoginParams {
  email: string;
  password: string;
  logas?: string;
}

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CustomFooterComponent, TranslateModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, AfterViewInit {
  isLoading = false;
  msg = '';

  username = '';

  adminMode = false;

  loginform: FormGroup;

  /**
   * Host for Google's own rendered button. Always in the DOM so it can be rendered into, and
   * revealed by `googleReady` only once Google actually drew a button in it.
   */
  @ViewChild('googleButton') googleButton?: ElementRef<HTMLDivElement>;

  googleReady = false;

  constructor(
    private service: LoginService,
    private authService: AuthService,
    private googleIdentity: GoogleIdentityService,
    private zone: NgZone,
    private fb: FormBuilder,
    private routes: Router,
    private translate: TranslateService
  ) {

    const stateEmail =
      typeof globalThis?.history?.state?.email === 'string' ? globalThis.history.state.email : '';
    if (stateEmail) {
      this.username = stateEmail;
    }

    this.loginform = this.fb.group({
      'email': ['', Validators.required],
      'password': ['', Validators.required],
      'logas': [''],
    });
    this.loginform.controls['email'].patchValue(this.username);

    if (this.routes.url.includes('admin')) this.adminMode = true;
  }

  /** Where the guards park the page an unauthenticated visitor was trying to reach. */
  private static readonly REDIRECT_KEY = 'redirectUrl';

  private static readonly DEFAULT_TARGET = '/home';

  ngOnInit(): void {
    // If user is already authenticated, don't make them log in again.
    const currentUser = this.authService.currentUserValue;
    if (currentUser) {
      this.redirectAfterLogin();
    }
  }

  ngAfterViewInit(): void {
    if (!this.googleButton) {
      return;
    }

    this.googleIdentity
      .renderButton(
        this.googleButton.nativeElement,
        // GIS calls back from outside Angular, so hop back in or the view won't update.
        credential => this.zone.run(() => this.signInWithGoogle(credential)),
        this.translate.currentLang
      )
      .then(() => (this.googleReady = true))
      .catch(error => {
        // Sign-in not configured, the client id call failed, or GIS didn't load. Password login
        // still works, so leave the block hidden rather than blocking the page.
        console.error('[LOGIN COMPONENT] Google sign-in unavailable:', error);
        this.googleReady = false;
      });
  }

  /**
   * Send the user where they were headed before the login page interrupted them.
   *
   * The guards already store that page in `redirectUrl` on their way here, but nothing ever read it
   * back, so every login landed on the home page and a deep link was silently lost. One method for
   * both the password and the Google path, so they can't drift apart on it.
   */
  private redirectAfterLogin(): void {
    const target = this.consumeStoredRedirect() ?? LoginComponent.DEFAULT_TARGET;
    this.routes.navigateByUrl(target);
  }

  /**
   * Read the stored destination and clear it, so it applies to this login only — otherwise a later
   * sign-in would be dragged back to a page the user has long since left.
   *
   * Only same-site absolute paths are accepted: `redirectUrl` is written from `state.url`, but it
   * lives in localStorage where anything could have put it, and a value naming another origin (an
   * absolute URL, or the protocol-relative `//host` a browser resolves as a host) would turn this
   * page into an open redirect. Anything unexpected is dropped in favour of the default.
   */
  private consumeStoredRedirect(): string | null {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(LoginComponent.REDIRECT_KEY);
      localStorage.removeItem(LoginComponent.REDIRECT_KEY);
    } catch {
      return null;
    }

    const value = (stored ?? '').trim();
    if (!value || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) {
      return null;
    }
    // Bouncing back to the login page would loop.
    if (value === '/login' || value.startsWith('/login?')) {
      return null;
    }
    return value;
  }

  private signInWithGoogle(credential: string): void {
    this.isLoading = true;
    this.msg = '';

    this.authService.loginWithGoogle(credential).subscribe({
      next: () => {
        this.isLoading = false;
        this.redirectAfterLogin();
      },
      error: err => {
        console.error('[LOGIN COMPONENT] Google login failed:', err);
        this.isLoading = false;
        this.showNotification(
          'error',
          this.translate.instant(err?.message || 'login.errors.googleSignInFailed')
        );
      }
    });
  }

  recoverform = false;
  public currentDomain = 'default';
  public claim = 'login.claim';

  showRecoverForm(event?: Event) {
    event?.preventDefault();
    const emailValue = this.loginform.get('email')?.value ?? '';
    this.routes.navigate(['/password-recovery'], {
      state: { email: emailValue }
    });
  }

  handleError(error: any) {
    let errorMessage = this.translate.instant('login.errors.invalidCredentials');
    if (error.error instanceof ErrorEvent) {
      // Client-side errors
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side errors
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    // window.alert(errorMessage);
    this.msg = errorMessage;
    return throwError(errorMessage);
  }

  check() {
    let params = this.loginform.value as LoginParams;

    if (params.email === '') {
      this.showNotification('error', this.translate.instant('login.errors.emptyEmail'));
    } else {
      this.isLoading = true;

      // Use AuthService instead of LoginService for proper authentication flow
      this.authService.login({ email: params.email, password: params.password }).subscribe({
        next: response => {
          this.isLoading = false;

          // Get current user data and redirect
          const currentUser = this.authService.currentUserValue;
          if (currentUser) {
            this.redirectAfterLogin();
          }
        },
        error: err => {
          console.error('[LOGIN COMPONENT] Login failed:', err);
          this.isLoading = false;

          // err.message e' una chiave di traduzione (vedi AuthService.resolveLoginError); se e'
          // un messaggio grezzo dell'API, ngx-translate lo restituisce invariato.
          this.showNotification(
            'error',
            this.translate.instant(err?.message || 'login.errors.invalidCredentials')
          );
        }
      });
    }
  }

  public showNotification( type: string, message: string ): void {
    this.msg = message;
  }


}
